/* eslint-disable jsx-a11y/anchor-is-valid */
import {FC, useEffect, useState, useRef} from 'react'
import {useIntl} from 'react-intl'
import {PageTitle} from '../../_metronic/layout/core'
import {toAbsoluteUrl} from '../../_metronic/helpers'
import {get} from '../resources/core/_requests'
import {useAuth} from '../auth'
import axios from "axios";

// TypeScript declarations untuk Leaflet
declare global {
  interface Window {
    L: any;
  }
}

const DashboardPage: FC = () => <></>

// Component untuk Leaflet Maps (OpenStreetMap - GRATIS)
const LeafletMapComponent: FC<{surveyData: any[]}> = ({surveyData}) => {
  const mapRef = useRef<HTMLDivElement>(null)
  const mapInstanceRef = useRef<any>(null)

  // Parse koordinat dari string "lat,lng"
  const parseCoordinates = (coordString: string) => {
    if (!coordString || coordString === 'NULL' || coordString === null) return null
    const [lat, lng] = coordString.split(',').map(coord => parseFloat(coord.trim()))
    return isNaN(lat) || isNaN(lng) ? null : {lat, lng}
  }

  useEffect(() => {
    // Load Leaflet CSS and JS
    const loadLeaflet = async () => {
      // Load CSS
      if (!document.querySelector('link[href*="leaflet"]')) {
        const cssLink = document.createElement('link')
        cssLink.rel = 'stylesheet'
        cssLink.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css'
        cssLink.integrity = 'sha256-p4NxAoJBhIIN+hmNHrzRCf9tD/miZyoHS5obTRR9BMY='
        cssLink.crossOrigin = ''
        document.head.appendChild(cssLink)
      }

      // Load JS
      if (!(window as any).L) {
        await new Promise((resolve) => {
          const script = document.createElement('script')
          script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js'
          script.integrity = 'sha256-20nQCchB9co0qIjJZRGuk2/Z9VM+kNiyxNV1lvTlZBo='
          script.crossOrigin = ''
          script.onload = resolve
          document.head.appendChild(script)
        })
      }

      initMap()
    }

    const initMap = () => {
      if (!mapRef.current || !(window as any).L) return

      // Clear existing map
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove()
      }

      // Default center (Jakarta, Indonesia)
      const map = (window as any).L.map(mapRef.current).setView([-6.2088, 106.8456], 6)
      mapInstanceRef.current = map

      // Add tile layer - OpenStreetMap
      ;(window as any).L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
        maxZoom: 19
      }).addTo(map)

      addMarkers(map)
    }

    const addMarkers = (map: any) => {
      const bounds = (window as any).L.latLngBounds()
      let hasMarkers = false

      surveyData.forEach(survey => {
        const locationCoord = parseCoordinates(survey.location)

        // Add Location marker (Blue)
        if (locationCoord) {
          const locationIcon = (window as any).L.divIcon({
            className: 'custom-marker-location',
            html: `<div style="
              background-color: #10b981;
              width: 20px;
              height: 20px;
              border-radius: 50%;
              border: 3px solid white;
              box-shadow: 0 2px 6px rgba(0,0,0,0.4);
              display: flex;
              align-items: center;
              justify-content: center;
              font-size: 10px;
              font-weight: bold;
              color: white;
            ">📍</div>`,
            iconSize: [20, 20],
            iconAnchor: [10, 10]
          })

          const locationMarker = (window as any).L.marker([locationCoord.lat, locationCoord.lng], {icon: locationIcon})
            .bindPopup(`
              <div style="padding: 10px; min-width: 200px;">
                <h6 style="margin: 0 0 10px 0; color: #10b981; font-weight: bold;">📍 Survey Location</h6>
                <div style="margin-bottom: 6px;"><strong>Order Code:</strong> ${survey.order_code}</div>
                <div style="margin-bottom: 6px;"><strong>Survey Code:</strong> ${survey.survey_code}</div>
                <div style="margin-bottom: 6px;"><strong>Koordinat:</strong> ${locationCoord.lat.toFixed(6)}, ${locationCoord.lng.toFixed(6)}</div>
                ${survey.created_by ? `<div style="margin-bottom: 6px;"><strong>Surveyor:</strong> ${survey.created_by}</div>` : ''}
                ${survey.created_time ? `<div><strong>Tanggal:</strong> ${new Date(survey.created_time).toLocaleDateString('id-ID')}</div>` : ''}
              </div>
            `, {
              maxWidth: 300
            })
            .addTo(map)

          bounds.extend([locationCoord.lat, locationCoord.lng])
          hasMarkers = true
        }
      })

      // Fit map to bounds if there are markers
      if (hasMarkers) {
        map.fitBounds(bounds, {padding: [20, 20], maxZoom: 16})
      }
    }

    loadLeaflet()

    // Cleanup function
    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove()
        mapInstanceRef.current = null
      }
    }
  }, [surveyData])

  const getValidLocationsCount = () => {
    let count = 0
    surveyData.forEach(survey => {
      if (parseCoordinates(survey.location)) count++
    })
    return count
  }

  return (
    <div className="position-relative">
      <div 
        ref={mapRef} 
        style={{height: '500px', width: '100%'}}
        className="rounded border"
      />
      
      {/* Map Controls */}
      <div className="position-absolute top-0 end-0 m-3" style={{zIndex: 1000}}>
        <div className="bg-white p-3 rounded shadow border">
          <div className="fw-bold mb-2">🗺️ Titik Lokasi</div>
          <div className="d-flex align-items-center gap-2 mb-2">
            <div style={{
              width: '16px', 
              height: '16px', 
              backgroundColor: '#10b981', 
              borderRadius: '50%',
              border: '2px solid white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '8px',
              fontWeight: 'bold',
              color: 'white'
            }}>📍</div>
            <span className="small">Survey Location</span>
          </div>
          <div className="border-top pt-2">
            <div className="small text-muted">📊 Total: {getValidLocationsCount()} Lokasi</div>
          </div>
        </div>
      </div>

      {/* Map Credits */}
      <div className="position-absolute bottom-0 start-0 m-2" style={{zIndex: 1000}}>
        <small className="text-muted bg-white px-2 py-1 rounded opacity-75">
          © OpenStreetMap Contributors
        </small>
      </div>
    </div>
  )
}

// Komponen Pagination untuk Tabel Survey
const SurveyPagination: FC<{
  currentPage: number
  totalPages: number
  totalItems: number
  itemsPerPage: number
  onPageChange: (page: number) => void
  onItemsPerPageChange: (items: number) => void
}> = ({ currentPage, totalPages, totalItems, itemsPerPage, onPageChange, onItemsPerPageChange }) => {
  return (
    <div className="d-flex justify-content-between align-items-center mt-4">
      <div className="d-flex align-items-center gap-2">
        <span className="text-muted">
          Menampilkan {Math.min((currentPage - 1) * itemsPerPage + 1, totalItems)}-
          {Math.min(currentPage * itemsPerPage, totalItems)} dari {totalItems} data
        </span>
      </div>

      <div className="d-flex align-items-center gap-2">
        <button
          className="btn btn-sm btn-light"
          onClick={() => onPageChange(1)}
          disabled={currentPage === 1}
        >
          {'<<'}
        </button>
        <button
          className="btn btn-sm btn-light"
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
        >
          {'<'}
        </button>

        <span className="mx-2 text-muted">
          Halaman {currentPage} dari {totalPages}
        </span>

        <button
          className="btn btn-sm btn-light"
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
        >
          {'>'}
        </button>
        <button
          className="btn btn-sm btn-light"
          onClick={() => onPageChange(totalPages)}
          disabled={currentPage === totalPages}
        >
          {'>>'}
        </button>
      </div>

      <div className="d-flex align-items-center gap-2">
        <span className="text-muted">Items per page:</span>
        <select
          className="form-select form-select-sm"
          style={{ width: '80px' }}
          value={itemsPerPage}
          onChange={(e) => onItemsPerPageChange(Number(e.target.value))}
        >
          {[5, 10, 20, 50].map((size) => (
            <option key={size} value={size}>
              {size}
            </option>
          ))}
        </select>
      </div>
    </div>
  )
}

const DashboardWrapper2: FC = () => {
  const intl = useIntl()
  const {currentUser} = useAuth()
  const [orderCount, setOrderCount] = useState<number>(0)
  const [branchCount, setBranchCount] = useState<number>(0)
  const [showroomCount, setShowroomCount] = useState<number>(0)
  const [kunjunganCount, setkunjunganCount] = useState<number>(0)
  const [debiturCount, setDebiturCount] = useState<number>(0)
  const [surveyCount, setSurveyCount] = useState<number>(0)
  const [masterCarCount, setMasterCarCount] = useState<number>(0)
  const [surveyData, setSurveyData] = useState<any[]>([])
  const [filteredSurveyData, setFilteredSurveyData] = useState<any[]>([])
  const [dateRange, setDateRange] = useState<{start: string, end: string}>({
    start: '',
    end: ''
  })
  const [isFiltering, setIsFiltering] = useState<boolean>(false)
  
  // State untuk pagination
  const [currentPage, setCurrentPage] = useState<number>(1)
  const [itemsPerPage, setItemsPerPage] = useState<number>(10)
  
  // State untuk export
  const [isExporting, setIsExporting] = useState<boolean>(false)

  const fetchCountOrder = async () => {
    try {
      const response = await get('orders')
      const entries = response.data.count
      setOrderCount(entries)
    } catch (e) {
      console.log(e)
    }
  }

  const fetchCountBranch = async () => {
    try {
      const response = await get('branch')
      const entries = response.data.count
      setBranchCount(entries)
    } catch (e) {
      console.log(e)
    }
  }

  const fetchCountShowroom = async () => {
    try {
      const response = await get('showroom')
      const entries = response.data.count
      setShowroomCount(entries)
    } catch (e) {
      console.log(e)
    }
  }

  const fetchCountKunjungan = async () => {
    try {
      const response = await get('visit')
      const entries = response.data.count
      setkunjunganCount(entries)
    } catch (e) {
      console.log(e)
    }
  }

  const fetchCountDebitur = async () => {
    try {
      const response = await get('debitur')
      const entries = response.data.count
      setDebiturCount(entries)
    } catch (e) {
      console.log(e)
    }
  }

  const fetchCountSurvey = async () => {
    try {
      const response = await get('survey')
      const entries = response.data.count
      setSurveyCount(entries)
    } catch (e) {
      console.log(e)
    }
  }

  const fetchCountMasterCar = async () => {
    try {
      const response = await get('assets_combine')
      const entries = response.data.count
      setMasterCarCount(entries)
    } catch (e) {
      console.log(e)
    }
  }

  // Fetch data survey dengan lokasi untuk map
  const fetchSurveyData = async () => {
    try {
      const response = await get('survey')
      const data = response.data.data || response.data || []
      setSurveyData(data)
      setFilteredSurveyData(data)
      setCurrentPage(1) // Reset ke halaman 1 ketika data berubah
    } catch (e) {
      console.log(e)
    }
  }

  // Filter data berdasarkan tanggal
  const filterByDate = () => {
    if (!dateRange.start && !dateRange.end) {
      setFilteredSurveyData(surveyData)
      setIsFiltering(false)
      return
    }

    const filtered = surveyData.filter(survey => {
      if (!survey.created_time) return false
      
      const surveyDate = new Date(survey.created_time)
      
      if (dateRange.start && dateRange.end) {
        const startDate = new Date(dateRange.start)
        const endDate = new Date(dateRange.end)
        endDate.setHours(23, 59, 59, 999) // Sampai akhir hari
        
        return surveyDate >= startDate && surveyDate <= endDate
      } else if (dateRange.start) {
        const startDate = new Date(dateRange.start)
        return surveyDate >= startDate
      } else if (dateRange.end) {
        const endDate = new Date(dateRange.end)
        endDate.setHours(23, 59, 59, 999)
        return surveyDate <= endDate
      }
      
      return true
    })

    setFilteredSurveyData(filtered)
    setIsFiltering(true)
    setCurrentPage(1) // Reset ke halaman 1 ketika filter diterapkan
  }

  // Reset filter tanggal
  const resetDateFilter = () => {
    setDateRange({start: '', end: ''})
    setFilteredSurveyData(surveyData)
    setIsFiltering(false)
    setCurrentPage(1) // Reset ke halaman 1 ketika filter direset
  }

  const apiBaseUrl: string = process.env.REACT_APP_API_BASE_URL || "";
  const apiVersion: string = process.env.REACT_APP_API_VERSION || "";

  // Fungsi untuk export data ke Excel
 
// Perbaikan untuk handleExportExcel function
// Perbaikan handleExportExcel - kirim filter yang sama
const handleExportExcel = async () => {
  try {
    setIsExporting(true);
    
    const apiBaseUrl: string = process.env.REACT_APP_API_BASE_URL || "";
    const apiVersion: string = process.env.REACT_APP_API_VERSION || "";
    
    // Build query parameters - PENTING: gunakan parameter yang sama dengan backend
    const queryParams = new URLSearchParams();
    
    if (dateRange.start) {
      queryParams.append('created_time', dateRange.start);
    }
    if (dateRange.end) {
      queryParams.append('created_time', dateRange.end);
    }
    
    const baseExportURL = `${apiBaseUrl}/${apiVersion}/table_survey/export/excel`;
    const fullExportURL = queryParams.toString() 
      ? `${baseExportURL}?${queryParams.toString()}`
      : baseExportURL;

    console.log('Export URL:', fullExportURL);
    
    const currentDate = new Date().toISOString().split("T")[0];
    const fileName = `export_survey_data_${currentDate}.xlsx`;

    const response = await axios.get(fullExportURL, {
      responseType: "blob",
    });

    const blob = new Blob([response.data], {
      type: response.headers["content-type"] || "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
    });
    
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    
  } catch (error) {
    console.error('Export error:', error);
    
    if (axios.isAxiosError(error)) {
      if (error.response?.status === 404) {
        alert('Endpoint export tidak ditemukan');
      } else if (error.response?.status === 500) {
        alert('Error di server. Silakan cek log backend.');
      } else {
        alert(`Gagal mengekspor data: ${error.response?.statusText || error.message}`);
      }
    } else {
      alert('Terjadi kesalahan saat mengekspor data');
    }
  } finally {
    setIsExporting(false);
  }
};


  // Fungsi untuk pagination
  const indexOfLastItem = currentPage * itemsPerPage
  const indexOfFirstItem = indexOfLastItem - itemsPerPage
  const currentItems = filteredSurveyData.slice(indexOfFirstItem, indexOfLastItem)
  const totalPages = Math.ceil(filteredSurveyData.length / itemsPerPage)

  const handlePageChange = (page: number) => {
    setCurrentPage(page)
  }

  const handleItemsPerPageChange = (items: number) => {
    setItemsPerPage(items)
    setCurrentPage(1) // Reset ke halaman 1 ketika items per page berubah
  }

  useEffect(() => {
    fetchCountOrder()
    fetchCountBranch()
    fetchCountShowroom()
    fetchCountKunjungan()
    fetchCountDebitur()
    fetchCountSurvey()
    fetchCountMasterCar()
    fetchSurveyData()
  }, [])

  return (
    <>
      <PageTitle breadcrumbs={[]}>{intl.formatMessage({id: 'MENU.DASHBOARD2'})}</PageTitle>
      <DashboardPage />
      <div className='card'>
        <div className='card-body py-4'>
          {/* Welcome Section */}
          <div hidden className='d-flex gap-6 align-items-center justify-content-center mb-6'>
          
            <div hidden>
              <div className='h2 fw-normal'>Welcome Back</div>
              <div className='display-6 text-blue'>
                {currentUser?.first_name || ''} {currentUser?.last_name || ''}
              </div>
            </div>
          </div>

          {/* Stats Cards */}
          <div className='row g-6 dashboard mb-6'>
            <div hidden className='col-4'>
              <div className='d-flex justify-content-between align-items-center p-4 dashboard-item'>
                <div className='d-flex gap-4 align-items-center'>
                  <img
                    alt='Logo'
                    src={toAbsoluteUrl('/media/icons/dashboard/car.png')}
                    height={48}
                  />
                  <h2 className='text-gray-700'>Master Car</h2>
                </div>
                <h3 className='display-5'>{masterCarCount}</h3>
              </div>
            </div>
            <div hidden className='col-4'>
              <div className='d-flex justify-content-between align-items-center p-4 dashboard-item'>
                <div className='d-flex gap-4 align-items-center'>
                  <img
                    alt='Logo'
                    src={toAbsoluteUrl('/media/icons/dashboard/order.png')}
                    height={48}
                  />
                  <h2 className='text-gray-700'>Order</h2>
                </div>
                <h3 className='display-5'>{orderCount}</h3>
              </div>
            </div>
            <div hidden className='col-4'>
              <div className='d-flex justify-content-between align-items-center p-4 dashboard-item'>
                <div className='d-flex gap-4 align-items-center'>
                  <img
                    alt='Logo'
                    src={toAbsoluteUrl('/media/icons/dashboard/branch.png')}
                    height={48}
                  />
                  <h2 className='text-gray-700'>Branch</h2>
                </div>
                <h3 className='display-5'>{branchCount}</h3>
              </div>
            </div>
            <div hidden className='col-4'>
              <div className='d-flex justify-content-between align-items-center p-4 dashboard-item'>
                <div className='d-flex gap-4 align-items-center'>
                  <img
                    alt='Logo'
                    src={toAbsoluteUrl('/media/icons/dashboard/showroom.png')}
                    height={48}
                  />
                  <h2 className='text-gray-700'>Showroom</h2>
                </div>
                <h3 className='display-5'>{showroomCount}</h3>
              </div>
            </div>
            <div hidden className='col-4'>
              <div className='d-flex justify-content-between align-items-center p-4 dashboard-item'>
                <div className='d-flex gap-4 align-items-center'>
                  <img
                    alt='Logo'
                    src={toAbsoluteUrl('/media/icons/dashboard/showroom.png')}
                    height={48}
                  />
                  <h2 className='text-gray-700'>Kunjungan Showroom</h2>
                </div>
                <h3 className='display-5'>{kunjunganCount}</h3>
              </div>
            </div>
            <div hidden className='col-4'>
              <div className='d-flex justify-content-between align-items-center p-4 dashboard-item'>
                <div className='d-flex gap-4 align-items-center'>
                  <img
                    alt='Logo'
                    src={toAbsoluteUrl('/media/icons/dashboard/survey.png')}
                    height={48}
                  />
                  <h2 className='text-gray-700'>Master Survey</h2>
                </div>
                <h3 className='display-5'>{surveyCount}</h3>
              </div>
            </div>
          </div>

          {/* Map Section */}
          <div className='row g-6 mb-6'>
            <div className='col-12'>
              <div className='card'>
                <div className='card-header border-0 pt-6'>
                  <div className='d-flex align-items-center gap-3'>
                    <i className='fa fa-map-marked-alt text-primary fs-1'></i>
                    <div>
                      <h3 className='card-title fw-bold mb-1'>🗺️ Peta Lokasi Survey</h3>
                      <div className='text-muted'>Menampilkan lokasi survey berdasarkan koordinat</div>
                    </div>
                    <div className="ms-auto">
                      <span className='badge badge-light-primary fs-6 px-3 py-2'>
                        📊 {filteredSurveyData.length} Total Survey
                        {isFiltering && ' (Difilter)'}
                      </span>
                    </div>
                  </div>
                  
                  {/* Filter Tanggal */}
                  <div className="row mt-4">
                    <div className="col-md-8">
                      <div className="d-flex align-items-center gap-2">
                        <label className="col-form-label fw-semibold">Filter Tanggal:</label>
                        <input
                          type="date"
                          className="form-control form-control-sm w-auto"
                          value={dateRange.start}
                          onChange={(e) => setDateRange({...dateRange, start: e.target.value})}
                        />
                        <span className="text-muted">s/d</span>
                        <input
                          type="date"
                          className="form-control form-control-sm w-auto"
                          value={dateRange.end}
                          onChange={(e) => setDateRange({...dateRange, end: e.target.value})}
                        />
                        <button 
                          className="btn btn-sm btn-primary"
                          onClick={filterByDate}
                        >
                          Terapkan
                        </button>
                        <button 
                          className="btn btn-sm btn-light"
                          onClick={resetDateFilter}
                          disabled={!dateRange.start && !dateRange.end}
                        >
                          Reset
                        </button>
                      </div>
                    </div>
                    <div className="col-md-4 text-end">
                      <div className="text-muted small">
                        Data dari: {surveyData.length} total survey
                      </div>
                    </div>
                  </div>
                </div>
                <div className='card-body py-4'>
                  <LeafletMapComponent surveyData={filteredSurveyData} />
                  
                  {/* Map Info */}
                  <div className="row mt-4">
                    <div className="col-md-3">
                      <div className="d-flex align-items-center gap-2 mb-2">
                        <i className="fa fa-map-marker text-primary"></i>
                        <span className="fw-bold">Fitur Peta:</span>
                      </div>
                      <ul className="list-unstyled small text-muted">
                        <li>• Zoom dan pan interaktif</li>
                        <li>• Popup info detail</li>
                        <li>• Marker lokasi survey</li>
                      </ul>
                    </div>
                    <div className="col-md-3">
                      <div className="d-flex align-items-center gap-2 mb-2">
                        <i className="fa fa-info-circle text-info"></i>
                        <span className="fw-bold">Panduan:</span>
                      </div>
                      <ul className="list-unstyled small text-muted">
                        <li>• Klik marker untuk detail</li>
                        <li>• Scroll untuk zoom in/out</li>
                        <li>• Drag untuk menggeser peta</li>
                      </ul>
                    </div>
                    <div className="col-md-3">
                      <div className="d-flex align-items-center gap-2 mb-2">
                        <i className="fa fa-chart-bar text-success"></i>
                        <span className="fw-bold">Statistik:</span>
                      </div>
                      <div className="small">
                        <div className="text-muted">Lokasi Valid: <span className="text-success fw-bold">
                          {filteredSurveyData.filter(s => s.location && s.location !== 'NULL').length}
                        </span></div>
                        <div className="text-muted">Total Survey: <span className="text-primary fw-bold">
                          {filteredSurveyData.length}
                        </span></div>
                      </div>
                    </div>
                    <div className="col-md-3">
                      <div className="d-flex align-items-center gap-2 mb-2">
                        <i className="fa fa-globe text-warning"></i>
                        <span className="fw-bold">Coverage:</span>
                      </div>
                      <div className="small text-muted">
                        <div>Indonesia & sekitarnya</div>
                        <div>Real-time coordinate</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Survey Data Table */}
          <div className='row g-6'>
            <div className='col-12'>
              <div className='card'>
                <div className='card-header border-0 pt-6'>
                  <div className='d-flex align-items-center justify-content-between'>
                    <div className='d-flex align-items-center gap-3'>
                      <i className='fa fa-table text-success fs-1'></i>
                      <div>
                        <h3 className='card-title fw-bold mb-1'>📊 Data Survey dengan Lokasi</h3>
                        <div className='text-muted'>
                          {isFiltering ? 'Data survey difilter berdasarkan tanggal' : 'Data survey dengan informasi koordinat'}
                        </div>
                      </div>
                    </div>
                    
                    {/* Tombol Export */}
                    <button 
                      onClick={handleExportExcel}
                      disabled={isExporting}
                      className="btn btn-primary"
                    >
                      {isExporting ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2"></span>
                          Exporting...
                        </>
                      ) : (
                        <>
                          <i className="fa fa-download me-2"></i>
                          Export Excel
                        </>
                      )}
                    </button>
                  </div>
                </div>
                <div className='card-body py-4'>
                  <div className='table-responsive'>
                    <table className='table table-row-dashed table-row-gray-300 gy-7'>
                      <thead>
                        <tr className='fw-bold fs-6 text-gray-800 border-bottom-2 border-gray-200'>
                          <th className="min-w-150px">Order Code</th>
                          <th className="min-w-150px">Survey Code</th>
                          <th className="min-w-150px">Tanggal Masuk Survey</th>
                          <th className="min-w-200px">📍 Location</th>
                          <th className="min-w-150px">Created By</th>
                          <th className="min-w-100px">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {currentItems.map((survey, index) => (
                          <tr key={survey.id || index} className="hover:bg-gray-50">
                            <td className="fw-bold text-gray-800">{survey.order_code}</td>
                            <td className="text-gray-600">{survey.survey_code}</td>
                            <td className="text-gray-600">
                              {survey.created_time ? new Date(survey.created_time).toLocaleDateString('id-ID') : '-'}
                            </td>
                            <td>
                              {survey.location && survey.location !== 'NULL' ? (
                                <div className='d-flex flex-column'>
                                  <div className='d-flex align-items-center gap-2 mb-1'>
                                    <div style={{
                                      width: '12px', 
                                      height: '12px', 
                                      backgroundColor: '#10b981', 
                                      borderRadius: '50%',
                                      border: '2px solid white',
                                      boxShadow: '0 1px 3px rgba(0,0,0,0.3)'
                                    }}></div>
                                    <span className='badge badge-light-success'>Ada Koordinat</span>
                                  </div>
                                  <span className='text-muted small font-monospace'>{survey.location}</span>
                                </div>
                              ) : (
                                <span className='badge badge-light-secondary'>📍 Tidak ada data</span>
                              )}
                            </td>
                            <td className="text-gray-600">{survey.created_by || '-'}</td>
                            <td>
                              {survey.location && survey.location !== 'NULL' ? (
                                <span className='badge badge-light-success'>
                                  <i className="fa fa-check-circle me-1"></i>
                                  Ada Lokasi
                                </span>
                              ) : (
                                <span className='badge badge-light-warning'>
                                  <i className="fa fa-exclamation-triangle me-1"></i>
                                  Belum Ada Lokasi
                                </span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  
                  {/* Pagination Component */}
                  {filteredSurveyData.length > 0 && (
                    <SurveyPagination
                      currentPage={currentPage}
                      totalPages={totalPages}
                      totalItems={filteredSurveyData.length}
                      itemsPerPage={itemsPerPage}
                      onPageChange={handlePageChange}
                      onItemsPerPageChange={handleItemsPerPageChange}
                    />
                  )}

                  {filteredSurveyData.length === 0 && (
                    <div className="text-center py-4">
                      <i className="fa fa-search fs-1 text-muted mb-2"></i>
                      <div className="text-muted">
                        {isFiltering ? 'Tidak ada data survey pada rentang tanggal yang dipilih' : 'Tidak ada data survey'}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export {DashboardWrapper2}